import React, { FC, useEffect, useMemo } from "react";
import cn from "classnames";
import stl from "./index.module.scss";
import { DefaultImage, Grid, Money, Quantity, Select } from "../../components";
import { Supplement } from "../../store/models";
import { SelectedSupplement } from "../../store/selected/selectedSupplements";
import { isArrayFilled, isPresent } from "../../utils";
import { SupplementsServingVariants } from "../../store/selected/shared";
import { useTranslation } from "react-i18next";
import { AppConfig } from "../../config";
import CheckIco from "../../assets/iconsComponents/Check";
import SupplementSkeleton from "./Skeleton";

interface Props {
  data: Supplement;
  selectedData?: SelectedSupplement;
  onQuantityChange?: (v: number | null) => void;
  servingVariants?: SupplementsServingVariants;
  onServingPlaceChange?: (v: {
    packageId?: number;
    activityId: number;
  }) => void;
}

const SupplementCard: FC<Props> & { Skeleton: FC } = ({
  data,
  selectedData,
  onQuantityChange,
  servingVariants,
  onServingPlaceChange,
}) => {
  const { t } = useTranslation(AppConfig.translationFileNames.step_Menu);
  const selected = isPresent(selectedData);
  const { name, description, avatar, serving_activities } = data;

  const encodeValue = (v: {
    packageId?: number | null;
    activityId?: number | null;
  }): string | undefined => {
    if (!isPresent(v.activityId)) return undefined;
    const res: { [k: string]: number } = {};

    if (v.activityId) res.activityId = v.activityId;
    if (v.packageId) res.packageId = v.packageId;

    return JSON.stringify(res);
  };

  const decodeValue = (
    v: string
  ): { packageId?: number; activityId: number } => {
    const vObject = JSON.parse(v);

    return {
      packageId: vObject.packageId,
      activityId: vObject.activityId,
    };
  };

  const servingValue = encodeValue({
    packageId: selectedData?.serving.packageId,
    activityId: selectedData?.serving.activityId,
  });

  // leaves packages and activities that are included in Supplement.serving_activities
  const filteredServingVariants = useMemo<
    SupplementsServingVariants | undefined
  >(() => {
    if (serving_activities.length === 0) return servingVariants;

    let activities =
      servingVariants?.activities?.filter((activity) => {
        return serving_activities.includes(activity.id);
      }) ?? null;

    if (activities && activities.length === 0) activities = null;

    const packages =
      servingVariants?.packages
        .map((package_) => {
          return {
            ...package_,
            activities: package_.activities.filter((activity) => {
              return serving_activities.includes(activity.id);
            }),
          };
        })
        .filter(({ activities }) => activities.length > 0) ?? [];

    return { activities, packages };
  }, [servingVariants, serving_activities]);

  useEffect(() => {
    if (selected === true && filteredServingVariants) {
      if (
        filteredServingVariants.activities &&
        filteredServingVariants.activities.length === 1 &&
        !isArrayFilled(filteredServingVariants.packages)
      ) {
        onServingPlaceChange?.({
          activityId: filteredServingVariants.activities[0].id,
        });
      }
      if (
        filteredServingVariants.packages &&
        filteredServingVariants.packages.length === 1 &&
        filteredServingVariants.packages[0].activities.length === 1 &&
        !isArrayFilled(filteredServingVariants.activities)
      ) {
        onServingPlaceChange?.({
          packageId: filteredServingVariants.packages[0].id,
          activityId: filteredServingVariants.packages[0].activities[0].id,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // hide supplement if can't be allocated anywhere
  if (
    serving_activities.length > 0 &&
    !filteredServingVariants?.activities &&
    !filteredServingVariants?.packages
  ) {
    return null;
  }

  return (
    <div className={cn(stl.block, { [stl.selected]: selected })}>
      <div className={stl.imgWrap}>
        <CheckIco className={stl.check} />
        {avatar ? (
          <img src={avatar} className={stl.img} alt="" />
        ) : (
          <DefaultImage className={stl.imgPlaceholder} />
        )}
      </div>
      <div className={stl.contentWrap}>
        <h3 className={stl.name}>{name}</h3>

        <p>{description}</p>

        <Grid container gutter={[10, 10]} className={stl.controls}>
          <Grid item sm={6} xs={12}>
            <div className={stl.quantityWrap}>
              <span className={stl.price}>
                <Money value={data.price} />
              </span>
              <Quantity
                className={stl.quantity}
                min={0}
                value={selectedData?.quantity ?? 0}
                onChange={onQuantityChange}
              />
            </div>
          </Grid>
          {selected && (
            <Grid item sm={6} xs={12}>
              <Select
                placeholder={t("supplementServing")}
                className={stl.selectServingActivity}
                value={servingValue}
                onChange={(v) => {
                  onServingPlaceChange?.(decodeValue(v as string));
                }}
              >
                {filteredServingVariants?.packages.map((package_) => (
                  <Select.OptGroup key={package_.name}>
                    {package_.activities.map((activity) => {
                      const value = encodeValue({
                        packageId: package_.id,
                        activityId: activity.id,
                      });

                      return (
                        <Select.Option
                          key={activity.name + package_.id + activity.id}
                          value={value as string}
                        >
                          {activity.name}
                        </Select.Option>
                      );
                    })}
                  </Select.OptGroup>
                ))}
                {filteredServingVariants?.activities && (
                  <Select.OptGroup key={"Separate activities"}>
                    {filteredServingVariants?.activities.map((activity) => {
                      const value = encodeValue({
                        activityId: activity.id,
                      });
                      return (
                        <Select.Option
                          key={activity.name + activity.id}
                          value={value as string}
                        >
                          {activity.name}
                        </Select.Option>
                      );
                    })}
                  </Select.OptGroup>
                )}
              </Select>
            </Grid>
          )}
        </Grid>
      </div>
    </div>
  );
};

SupplementCard.Skeleton = SupplementSkeleton;

export default SupplementCard;
